/**
 * Franchise URL Mapping Configuration
 *
 * Maps domain/hostname to franchiseId to automatically associate users
 * with the correct franchise based on the URL they access the app from.
 *
 * CRITICAL: Each franchise MUST have a unique URL to prevent users from
 * seeing branches/queues from other franchises.
 */

export interface FranchiseUrlMapping {
  /** The domain/hostname (without protocol) */
  hostname: string;
  /** The Firebase Firestore franchiseId */
  franchiseId: string;
  /** Display name for debugging */
  franchiseName: string;
}

/**
 * Production franchise URL mappings
 *
 * Add new franchises here when they are created.
 * Each franchise MUST have a unique domain.
 */
export const FRANCHISE_URL_MAPPINGS: FranchiseUrlMapping[] = [
  {
    hostname: 'monkeybarber.club',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop',
  },
  {
    hostname: 'www.monkeybarber.club',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop',
  },
  // Add more franchises here as they are created
  // {
  //   hostname: 'yourbarber.com',
  //   franchiseId: 'your-franchise-id',
  //   franchiseName: 'Your Barber Shop',
  // },
];

/**
 * Development/testing URLs that map to franchises
 * These are used in localhost and Firebase Hosting preview URLs
 */
const DEV_MAPPINGS: FranchiseUrlMapping[] = [
  {
    hostname: 'localhost',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Dev)',
  },
  {
    hostname: 'monkeybarberapp.web.app',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Firebase)',
  },
  {
    hostname: 'monkeybarberapp.firebaseapp.com',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Firebase)',
  },
  // Entorno de desarrollo (proyecto Firebase jarvis-alarm-44896).
  // Mismo franchiseId porque el catálogo es un clon íntegro de producción.
  // Ver docs/environments/DEV_ENVIRONMENT.md
  {
    hostname: 'jarvis-alarm-44896.web.app',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Dev)',
  },
  {
    hostname: 'jarvis-alarm-44896.firebaseapp.com',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Dev)',
  },
  // Legacy (old project)
  {
    hostname: 'comprakitsupervivencia.web.app',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Legacy)',
  },
  {
    hostname: 'comprakitsupervivencia.firebaseapp.com',
    franchiseId: 'lILDGxMgfsifzpqg4YZu',
    franchiseName: 'Monkey Barber Shop (Legacy)',
  },
];

/**
 * Get franchiseId from current URL hostname
 *
 * @returns franchiseId if found, undefined otherwise
 */
export function getFranchiseIdFromUrl(): string | undefined {
  const hostname = window.location.hostname;

  // Check production mappings first
  const prodMapping = FRANCHISE_URL_MAPPINGS.find(m => m.hostname === hostname);
  if (prodMapping) {
    console.log(`🏢 Franchise detected from URL: ${prodMapping.franchiseName} (${prodMapping.franchiseId})`);
    return prodMapping.franchiseId;
  }

  // Check dev mappings
  const devMapping = DEV_MAPPINGS.find(m => m.hostname === hostname);
  if (devMapping) {
    console.log(`🏢 Franchise detected from URL (DEV): ${devMapping.franchiseName} (${devMapping.franchiseId})`);
    return devMapping.franchiseId;
  }

  console.warn(`⚠️ No franchise mapping found for hostname: ${hostname}`);
  console.warn(`⚠️ User will see ALL branches from ALL franchises!`);
  console.warn(`⚠️ Add this hostname to src/config/franchiseUrls.ts`);

  return undefined;
}

/**
 * Get franchise info from current URL
 *
 * @returns Full franchise mapping info if found, undefined otherwise
 */
export function getFranchiseInfoFromUrl(): FranchiseUrlMapping | undefined {
  const hostname = window.location.hostname;

  return (
    FRANCHISE_URL_MAPPINGS.find(m => m.hostname === hostname) ||
    DEV_MAPPINGS.find(m => m.hostname === hostname)
  );
}

/**
 * Validates that the current URL has a franchise mapping
 * Logs warning if not found
 *
 * @returns true if franchise mapping exists, false otherwise
 */
export function validateFranchiseUrl(): boolean {
  const franchiseId = getFranchiseIdFromUrl();
  return franchiseId !== undefined;
}
